import { useCallback, useEffect, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useTrackSubmittedCollectionForm } from '@air/analytics';
import { ShortUrl } from '@air/api';
import { MAX_ANCESTOR_COUNT_EXCEEDED, MAX_FORM_SESSION_ITEMS_EXCEEDED, matchesAirror } from '@air/errors';
import {
  contentCollectionClipsSelector,
  contentCollectionFormSelector,
  contentCollectionPendingAutoSubmitCountSelector,
  contentCollectionSessionIdSelector,
  contentCollectionSubmitterNameSelector,
  setAutoSubmitStatusAction,
} from '@air/feature-content-collection/redux';
import { useShortIdContext } from '@air/provider-short-id';
import { useToasts } from '@air/provider-toast';
import { UploadStatus } from '@air/redux-uploader';
import { callRequestWithRetry } from '@air/upload-utils';
import { reportErrorToBugsnag } from '@air/utils-error';

import { useAirStore } from '~/utils/ReduxUtils';

const MAX_BATCH_SIZE = 50;
const DEBOUNCE_INTERVAL_MS = 200;

export const useContentCollectionAutoSubmitBatcher = () => {
  const store = useAirStore();
  const dispatch = useDispatch();
  const { shortId } = useShortIdContext();
  const { showToast } = useToasts();
  const { trackSubmittedCollectionForm } = useTrackSubmittedCollectionForm();
  const form = useSelector(contentCollectionFormSelector);
  const pendingCount = useSelector(contentCollectionPendingAutoSubmitCountSelector);

  const isSubmittingRef = useRef(false);

  const submitBatch = useCallback(async () => {
    const sessionId = contentCollectionSessionIdSelector(store.getState());

    if (!form.autoSubmit || !sessionId || isSubmittingRef.current) {
      return;
    }

    const pendingClips = contentCollectionClipsSelector(store.getState()).filter(
      (clip) => clip.autoSubmit?.status === 'pending' && clip.upload.status === UploadStatus.completed,
    );

    if (pendingClips.length === 0) {
      return;
    }
    isSubmittingRef.current = true;
    const batchClips = pendingClips.slice(0, MAX_BATCH_SIZE);

    try {
      await callRequestWithRetry(() =>
        ShortUrl.submitFormSession({
          shortId,
          formId: form.id,
          sessionId,
          name: contentCollectionSubmitterNameSelector(store.getState()),
          assetIds: batchClips.map((clip) => clip.assetId),
        }),
      );

      batchClips.forEach((clip) => {
        dispatch(setAutoSubmitStatusAction({ clipId: clip.id, status: 'success' }));
      });

      trackSubmittedCollectionForm({ auto_submit: true });
    } catch (error) {
      let message = 'Failed to submit file';
      let retryable = true;

      if (matchesAirror(error, MAX_ANCESTOR_COUNT_EXCEEDED)) {
        showToast(
          "The folder structure you're trying to upload to is too deep. Please simplify your folder structure and try again.",
          { color: 'red' },
        );
        message = 'Maximum folder depth reached';
        retryable = false;
      } else if (matchesAirror(error, MAX_FORM_SESSION_ITEMS_EXCEEDED)) {
        showToast("You've reached the maximum number of items you can upload to this collection.", {
          color: 'red',
        });
        message = 'Maximum number of items reached';
        retryable = false;
      }
      reportErrorToBugsnag({
        error,
        context: 'Failed to auto-submit content collection clip batch',
        metadata: { shortId, formId: form.id, sessionId, clipIds: batchClips.map((clip) => clip.id) },
      });
      batchClips.forEach((clip) => {
        dispatch(setAutoSubmitStatusAction({ clipId: clip.id, status: 'failure', message, retryable }));
      });
    }

    isSubmittingRef.current = false;
  }, [store, dispatch, showToast, form, shortId, trackSubmittedCollectionForm]);

  useEffect(() => {
    if (pendingCount === 0 || isSubmittingRef.current) {
      return;
    }

    const timeoutId = setTimeout(() => {
      void submitBatch();
    }, DEBOUNCE_INTERVAL_MS);

    return () => clearTimeout(timeoutId);
  }, [pendingCount, submitBatch]);
};
